import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Skeleton,
  Typography,
  Link,
  IconButton,
  Tooltip,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { motion, AnimatePresence } from 'framer-motion';
import Sidebar from './Sidebar.jsx';
import { useDispatch, useSelector } from 'react-redux';
import { fetchWorkflowRunHistoryThunk } from '../../state/slices/dashboardSlice';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  Legend,
} from 'recharts';
import WorkflowConnectionsDialog from '../../components/WorkflowConnectionsDialog.jsx';
import PropTypes from 'prop-types';
import LogsViewer from '../../workflows/LogsViewer';
import OnboardingTour from '../../components/OnboardingTour';

const pad = (n) => String(n).padStart(2, '0');
const formatLocalDate = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const getWeeklyDateRange = () => {
  const to = new Date();
  const from = new Date();
  from.setDate(to.getDate() - 7);
  return { from: formatLocalDate(from), to: formatLocalDate(to) };
};

const getDailyDateRange = (dateStr) => {
  const d = dateStr ? new Date(dateStr) : new Date();
  const date = formatLocalDate(d);
  return { from: date, to: date };
};

const statusColorMap = {
  SUCCESS: {
    bg: '#69C86D',
    bgLight: '#D1FAE5',
    text: '#065F46',
    border: '#69C86D',
  },
  FAILED: {
    bg: '#EF4444',
    bgLight: '#FEE2E2',
    text: '#991B1B',
    border: '#EF4444',
  },
  ACTIVE: {
    bg: '#2F5D63',
    bgLight: '#DDF3FA',
    text: '#222222',
    border: '#2F5D63',
  },
  PARTIAL_SUCCESS: {
    bg: '#F59E0B',
    bgLight: '#FEF3C7',
    text: '#92400E',
    border: '#F59E0B',
  },
  CANCELLED: {
    bg: '#A3A3A3',
    bgLight: '#E5E7EB',
    text: '#1F2937',
    border: '#A3A3A3',
  },
};

const Reports = () => {
  const dispatch = useDispatch();
  const { workflowRunHistory: storeRunHistory = [] } = useSelector(
    (state) => state.dashboard || {}
  );
  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.4,
        type: 'spring',
        stiffness: 100,
      },
    },
  };

  const rowVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: (index) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: index * 0.05,
        duration: 0.3,
        ease: 'easeOut',
      },
    }),
    hover: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.2,
        ease: 'easeInOut',
      },
    },
  };

  const { from: initialFrom, to: initialTo } = getWeeklyDateRange();
  const [filters, setFilters] = useState({
    range: 'weekly',
    from_date: initialFrom,
    to_date: initialTo,
    limit: 10,
    runMode: 'ALL',
    recordType: 'ALL',
  });
  const [search, setSearch] = useState('');
  const [connOpen, setConnOpen] = useState(false);
  const [connWorkflowName, setConnWorkflowName] = useState('');
  const [connLoading, setConnLoading] = useState(false);
  const [connError, setConnError] = useState('');
  const [connData, setConnData] = useState(null);

  // Logs viewer state
  const [logsDialogOpen, setLogsDialogOpen] = useState(false);
  const [selectedWorkflowRunId, setSelectedWorkflowRunId] = useState(null);

  // Data state
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [workflowConnections, setWorkflowConnections] = useState({});
  
  // Sorting state for table
  const [sortConfig, setSortConfig] = useState({
    column: null,
    direction: 'none'
  });

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        range: filters.range,
        limit: filters.limit,
        from_date: filters.from_date || undefined,
        to_date: filters.to_date || undefined,
      };
      // Always fetch fresh run history to ensure latest workflows are visible
      const data = await dispatch(fetchWorkflowRunHistoryThunk(params)).unwrap();
      setRows(Array.isArray(data) ? data : []);
      // Build connections map from run-history metadata (no separate API calls)
      if (Array.isArray(data) && data.length > 0) {
        const connectionsMap = {};
        for (const run of data) {
          const wfId = run?.workflow_id;
          const meta = run?.metadata || {};
          const sources = Array.isArray(meta.sources) ? meta.sources : [];
          const destinations = Array.isArray(meta.destinations)
            ? meta.destinations
            : [];
          if (wfId) {
            connectionsMap[wfId] = {
              connections: { sources, destinations },
            };
          }
        }
        setWorkflowConnections(connectionsMap);
      }
    } catch (e) {
      setError(e?.message || 'Failed to fetch run history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleChange = (key) => (e) => {
    setFilters((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const handleRangeChange = (e) => {
    const value = e.target.value;
    if (value === 'weekly') {
      const { from, to } = getWeeklyDateRange();
      setFilters((prev) => ({
        ...prev,
        range: value,
        from_date: from,
        to_date: to,
        limit: 10,
      }));
    } else if (value === 'daily') {
      const { from, to } = getDailyDateRange();
      setFilters((prev) => ({
        ...prev,
        range: value,
        from_date: from,
        to_date: to,
        limit: 10,
      }));
    } else {
      setFilters((prev) => ({ ...prev, range: value }));
    }
  };

  const handleFromDateChange = (e) => {
    const val = e.target.value;
    setFilters((prev) =>
      prev.range === 'daily'
        ? { ...prev, from_date: val, to_date: val }
        : { ...prev, from_date: val },
    );
  };

  const handleToDateChange = (e) => {
    const val = e.target.value;
    setFilters((prev) =>
      prev.range === 'daily'
        ? { ...prev, from_date: val, to_date: val }
        : { ...prev, to_date: val },
    );
  };


  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = rows;
    if (q) {
      list = list.filter((r) =>
        (r?.workflow_name || '').toLowerCase().includes(q),
      );
    }


    if (filters.runMode === 'SCHEDULED') {
      list = list.filter((r) => !!r?.is_scheduled);
    } else if (filters.runMode === 'ON DEMAND') {
      list = list.filter((r) => !r?.is_scheduled);
    }

  
    if (filters.recordType === 'SUCCESS') {
      list = list.filter((r) => Number(r?.success_record_count || 0) > 0);
    } else if (filters.recordType === 'FAILED') {
      list = list.filter((r) => Number(r?.failure_record_count || 0) > 0);
    }

    return list;
  }, [rows, search, filters.runMode, filters.recordType]);
  
  // Helper function to get connection names
  const getConnectionNames = (workflowId) => {
    const connData = workflowConnections[workflowId];
    if (!connData || !connData.connections) {
      return { source: [], destination: [] };
    }

    // Extract source connection names
    const sources = connData.connections.sources || [];
    const sourceNames = sources
      .map(s => s?.connection_name)
      .filter(Boolean);

    // Extract destination connection names
    const destinations = connData.connections.destinations || [];
    const destNames = destinations
      .map(d => d?.connection_name)
      .filter(Boolean);

    return { 
      source: sourceNames.length > 0 ? sourceNames : ['-'], 
      destination: destNames.length > 0 ? destNames : ['-'] 
    };
  };
  
  // Apply sorting to filteredRows
  const sortedRows = useMemo(() => {
    if (sortConfig.direction === 'none' || !sortConfig.column) {
      return filteredRows;
    }
    
    return [...filteredRows].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortConfig.column) {
        case 'workflow_name':
          aValue = (a.workflow_name || '').toLowerCase();
          bValue = (b.workflow_name || '').toLowerCase();
          break;
        case 'status':
          aValue = (a.status || a.run_status || '').toLowerCase();
          bValue = (b.status || b.run_status || '').toLowerCase();
          break;
        case 'run_mode':
          aValue = (a.is_scheduled ? 'scheduled' : 'on demand');
          bValue = (b.is_scheduled ? 'scheduled' : 'on demand');
          break;
        case 'date':
          aValue = a.start_time ? new Date(a.start_time).getTime() : 0;
          bValue = b.start_time ? new Date(b.start_time).getTime() : 0;
          break;
        case 'source':
          {
            const aSource = getConnectionNames(a.workflow_id).source;
            const bSource = getConnectionNames(b.workflow_id).source;
            aValue = (aSource[0] === '-' ? '' : aSource[0] || '').toLowerCase();
            bValue = (bSource[0] === '-' ? '' : bSource[0] || '').toLowerCase();
          }
          break;
        case 'destination':
          {
            const aDest = getConnectionNames(a.workflow_id).destination;
            const bDest = getConnectionNames(b.workflow_id).destination;
            aValue = (aDest[0] === '-' ? '' : aDest[0] || '').toLowerCase();
            bValue = (bDest[0] === '-' ? '' : bDest[0] || '').toLowerCase();
          }
          break;
        case 'success':
          aValue = Number(a.success_record_count || 0);
          bValue = Number(b.success_record_count || 0);
          break;
        case 'failure':
          aValue = Number(a.failure_record_count || 0);
          bValue = Number(b.failure_record_count || 0);
          break;
        default:
          return 0;
      }
      
      if (sortConfig.column === 'date' || sortConfig.column === 'success' || sortConfig.column === 'failure') {
        // For dates and numbers
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      } else {
        // For strings
        if (sortConfig.direction === 'asc') {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      }
    });
  }, [filteredRows, sortConfig, workflowConnections]);

  const groupedWorkflows = useMemo(() => {
    const groups = new Map();

    sortedRows.forEach((row) => {
      const key = `${row.workflow_name}_${row.workflow_id}`;
      if (!groups.has(key)) {
        groups.set(key, {
          workflow_name: row.workflow_name,
          workflow_id: row.workflow_id,
          runs: [],
          runCount: 0,
        });
      }
      const group = groups.get(key);
      group.runs.push(row);
      group.runCount = group.runs.length;
    });

    return Array.from(groups.values());
  }, [sortedRows]);

  const toggleGroup = (workflowName, workflowId) => {
    const key = `${workflowName}_${workflowId}`;
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };
  
  // Sorting handler
  const handleSort = (column) => {
    let direction = 'asc';
    
    if (sortConfig.column === column && sortConfig.direction === 'asc') {
      direction = 'desc';
    } else if (sortConfig.column === column && sortConfig.direction === 'desc') {
      direction = 'none';
      column = null;
    }
    
    setSortConfig({ column, direction });
  };

  const workflowAgg = useMemo(() => {
    const map = new Map();
    sortedRows.forEach((r) => {
      const name = r?.workflow_name || 'Unknown';
      const s = Number(r?.success_record_count || 0);
      const f = Number(r?.failure_record_count || 0);
      const id = r?.workflow_id;
      const cur = map.get(name) || { success: 0, failure: 0, workflow_id: id };
      cur.success += s;
      cur.failure += f;
      if (!cur.workflow_id && id) cur.workflow_id = id;
      map.set(name, cur);
    });
    const rows = Array.from(map.entries()).map(([name, v]) => ({
      name,
      success: v.success,
      failure: v.failure,
      total: v.success + v.failure,
      workflow_id: v.workflow_id,
    }));
    rows.sort((a, b) => b.total - a.total);
    const maxTotal = rows.reduce((m, a) => Math.max(m, a.total), 0);
    return { rows, maxTotal };
  }, [sortedRows]);


  const WorkflowYAxisTick = ({ x, y, payload }) => (
    <text x={x} y={y} textAnchor="end" fill="#222222" style={{ fontSize: 12, fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
      {payload?.value}
    </text>
  );

  WorkflowYAxisTick.propTypes = {
    x: PropTypes.number,
    y: PropTypes.number,
    payload: PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      index: PropTypes.number,
    }),
  };

  const openConnectionsById = async (id, name) => {
    setConnError('');
    setConnWorkflowName(name || '');
    setConnOpen(true);
    
    if (!id) {
      setConnError('Workflow ID not found.');
      setConnData(null);
      return;
    }
    
    // Check if we already have the data in workflowConnections state
    if (workflowConnections[id]) {
      setConnData(workflowConnections[id]);
      return;
    }
    // Derive from available run-history data; fetch via thunk only if missing
    try {
      setConnLoading(true);
      let derived = null;

      const sourceData = (Array.isArray(rows) && rows.length > 0
        ? rows
        : storeRunHistory
      ).find((run) => run?.workflow_id === id)?.metadata;

      if (sourceData) {
        const sources = Array.isArray(sourceData.sources)
          ? sourceData.sources
          : [];
        const destinations = Array.isArray(sourceData.destinations)
          ? sourceData.destinations
          : [];
        derived = { connections: { sources, destinations } };
      }

      if (!derived && (!storeRunHistory || storeRunHistory.length === 0)) {
        const fetched = await dispatch(
          fetchWorkflowRunHistoryThunk({
            range: filters.range,
            limit: Math.max(filters.limit || 10, 100),
            from_date: filters.from_date || undefined,
            to_date: filters.to_date || undefined,
          })
        ).unwrap();

        const meta = fetched
          ?.find((run) => run?.workflow_id === id)
          ?.metadata;
        if (meta) {
          const sources = Array.isArray(meta.sources) ? meta.sources : [];
          const destinations = Array.isArray(meta.destinations)
            ? meta.destinations
            : [];
          derived = { connections: { sources, destinations } };
        }
      }

      if (derived) {
        setConnData(derived);
        setWorkflowConnections((prev) => ({ ...prev, [id]: derived }));
      } else {
        setConnError('Connections not available in run history');
        setConnData(null);
      }
    } catch (e) {
      setConnError(e?.message || 'Failed to derive connections');
    } finally {
      setConnLoading(false);
    }
  };

  const handleViewLogs = (workflowRunId) => {
    setSelectedWorkflowRunId(workflowRunId);
    setLogsDialogOpen(true);
  };

  const handleDownloadCSV = () => {

    const headers = [
      'Sr. No',
      'Workflow Name',
      'Status',
      'Run Mode',
      'Date',
      'Source',
      'Destination',
      'Success Records',
      'Failure Records',
      'Workflow Run ID',
    ];

    // Prepare CSV rows
    const csvRows = sortedRows.map((row, idx) => {
      const runMode = row?.is_scheduled ? 'SCHEDULED' : 'ON DEMAND';
      const startDate = row.start_time
        ? formatLocalDate(new Date(row.start_time))
        : '-';
      const { source, destination } = getConnectionNames(row.workflow_id);

      return [
        idx + 1,
        `"${row.workflow_name || ''}"`,
        row.status || row.run_status || 'N/A',
        runMode,
        startDate,
        `"${source.join('; ')}"`,
        `"${destination.join('; ')}"`,
        row.success_record_count ?? 0,
        row.failure_record_count ?? 0,
        row.workflow_run_id || '',
      ].join(',');
    });

    // Combine headers and rows
    const csvContent = [headers.join(','), ...csvRows].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `workflow_run_history_${new Date().toISOString().split('T')[0]}.csv`,
    );
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <motion.div variants={containerVariants} initial="hidden" animate="visible">
      <Box sx={{ display: 'flex' }}>
        <Sidebar />
        <Box
          sx={{
            flex: 1,
            p: 3,
            background: 'linear-gradient(180deg, #DDF3FA 0%, #F4FBFD 60%, #FFFFFF 100%)',
            minHeight: '100vh',
          }}
        >
          {/* Header */}
          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{
                fontSize: '1.75rem',
                fontWeight: 700,
                color: '#222222',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
              }}
            >
              Reports
            </Typography>
          </Box>

          {/* Filters */}
          <Paper
            elevation={0}
            data-tour="reports-filters"
            sx={{
              p: 3,
              border: '1px solid #E6E6E6',
              borderRadius: 2,
              mb: 2,
              background: 'linear-gradient(to bottom, #ffffff, #F9FAFB)',
            }}
          >
            <Typography
              sx={{
                fontSize: '0.95rem',
                fontWeight: 600,
                color: '#222222',
                mb: 2.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 4,
                  height: 20,
                  backgroundColor: '#2F5D63',
                  borderRadius: 1,
                  display: 'inline-block',
                }}
              />
              Filter Options
            </Typography>

            <Box
              sx={{
                display: 'flex',
                gap: 1.5,
                flexWrap: 'wrap',
                alignItems: 'center',
              }}
            >
              <FormControl
                size="small"
                sx={{
                  minWidth: 120,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#ffffff',
                  },
                }}
              >
                <InputLabel id="range-label">Range</InputLabel>
                <Select
                  labelId="range-label"
                  value={filters.range}
                  label="Range"
                  onChange={handleRangeChange}
                >
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                </Select>
              </FormControl>

              <TextField
                size="small"
                label="From"
                type="date"
                value={filters.from_date}
                onChange={handleFromDateChange}
                sx={{
                  minWidth: 155,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#ffffff',
                    '&:hover': {
                      backgroundColor: '#F9FAFB',
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#ffffff',
                    },
                  },
                }}
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                size="small"
                label="To"
                type="date"
                value={filters.to_date}
                onChange={handleToDateChange}
                sx={{
                  minWidth: 155,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#ffffff',
                    '&:hover': {
                      backgroundColor: '#F9FAFB',
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#ffffff',
                    },
                  },
                }}
                InputLabelProps={{ shrink: true }}
              />

              <FormControl
                size="small"
                sx={{
                  minWidth: 130,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#ffffff',
                    '&:hover': {
                      backgroundColor: '#F9FAFB',
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#ffffff',
                    },
                  },
                }}
              >
                <InputLabel id="runmode-label">Run Mode</InputLabel>
                <Select
                  labelId="runmode-label"
                  value={filters.runMode}
                  label="Run Mode"
                  onChange={handleChange('runMode')}
                >
                  <MenuItem value="ALL">All</MenuItem>
                  <MenuItem value="SCHEDULED">Scheduled</MenuItem>
                  <MenuItem value="ON DEMAND">On Demand</MenuItem>
                </Select>
              </FormControl>

              <FormControl
                size="small"
                sx={{
                  minWidth: 130,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#ffffff',
                    '&:hover': {
                      backgroundColor: '#F9FAFB',
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#ffffff',
                    },
                  },
                }}
              >
                <InputLabel id="records-label">Records</InputLabel>
                <Select
                  labelId="records-label"
                  value={filters.recordType}
                  label="Records"
                  onChange={handleChange('recordType')}
                >
                  <MenuItem value="ALL">All</MenuItem>
                  <MenuItem value="SUCCESS">Success</MenuItem>
                  <MenuItem value="FAILED">Failed</MenuItem>
                </Select>
              </FormControl>

              <TextField
                size="small"
                label="Limit"
                type="number"
                value={filters.limit}
                onChange={handleChange('limit')}
                inputProps={{ min: 1, max: 100 }}
                sx={{
                  width: 90,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#ffffff',
                    '&:hover': {
                      backgroundColor: '#F9FAFB',
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#ffffff',
                    },
                  },
                }}
              />

              <TextField
                size="small"
                label="Search"
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{
                  flex: 1,
                  minWidth: 180,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#ffffff',
                    '&:hover': {
                      backgroundColor: '#F9FAFB',
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#ffffff',
                    },
                  },
                }}
              />

              <Button
                variant="contained"
                onClick={loadData}
                disabled={loading}
                sx={{
                  minWidth: 95,
                  height: 40,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  borderRadius: '8px',
                  boxShadow: 'none',
                  background:
                    'linear-gradient(120deg, #69C86D 0%, #4EB3A2 45%, #2F5D63 100%)',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(105, 200, 109, 0.3)',
                    background:
                      'linear-gradient(120deg, #5AB85F 0%, #3FA390 45%, #1F4F55 100%)',
                  },
                  '&:active': {
                    transform: 'translateY(1px)',
                  },
                }}
              >
                {loading ? 'Loading...' : 'Apply'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  const { from, to } = getWeeklyDateRange();
                  setFilters({
                    range: 'weekly',
                    from_date: from,
                    to_date: to,
                    limit: 10,
                    runMode: 'ALL',
                    recordType: 'ALL',
                  });
                  setSearch('');
                  setTimeout(loadData, 0);
                }}
                disabled={loading}
                sx={{
                  minWidth: 80,
                  height: 40,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  borderRadius: '8px',
                  borderColor: '#E6E6E6',
                  color: '#222222',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                  '&:hover': {
                    borderColor: '#2F5D63',
                    backgroundColor: '#DDF3FA',
                  },
                }}
              >
                Reset
              </Button>
            </Box>
          </Paper>

          {/* Workflow Breakdown (Recharts horizontal grouped bars) */}
          <Paper
            elevation={0}
            data-tour="reports-chart"
            sx={{
              p: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              mb: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1,
              }}
            >
              <Typography
                sx={{ 
                  fontSize: '0.95rem', 
                  fontWeight: 600, 
                  color: '#222222',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                }}
              >
                Workflow Breakdown
              </Typography>
              <Typography sx={{ 
                fontSize: '0.75rem', 
                color: '#222222',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
              }}>
                Total Records: Success{' '}
                {workflowAgg.rows
                  .reduce((sum, row) => sum + row.success, 0)
                  .toLocaleString()}{' '}
                | Failed{' '}
                {workflowAgg.rows
                  .reduce((sum, row) => sum + row.failure, 0)
                  .toLocaleString()}
              </Typography>
            </Box>

            {workflowAgg.rows.length === 0 ? (
              <Typography sx={{ 
                color: '#222222', 
                fontSize: '0.85rem',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
              }}>
                No workflow data for current filters.
              </Typography>
            ) : (
              <Box
                sx={{
                  height: Math.min(
                    480,
                    48 * Math.min(filters.limit, workflowAgg.rows.length),
                  ),
                }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    layout="vertical"
                    data={workflowAgg.rows.slice(0, filters.limit)}
                    margin={{ top: 8, right: 16, bottom: 8, left: 0 }}
                  >
                    <YAxis
                      type="category"
                      dataKey="name"
                      width={180}
                      tick={<WorkflowYAxisTick />}
                    />
                    <XAxis type="number" tick={{ fontSize: 12 }} />
                    <RechartsTooltip
                      formatter={(value, name) => [
                        Number(value).toLocaleString(),
                        name,
                      ]}
                    />
                    <Legend />
                    <Bar dataKey="success" name="success" fill="#69C86D" />
                    <Bar dataKey="failure" name="failed" fill="#EF4444" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Paper>

          <WorkflowConnectionsDialog
            open={connOpen}
            onClose={() => setConnOpen(false)}
            loading={connLoading}
            error={connError}
            data={connData}
            workflowName={connWorkflowName}
          />

          {/* Table */}
          <motion.div variants={itemVariants}>
            <Box
              component={motion.div}
              data-tour="reports-table"
              sx={{
                borderRadius: '12px',
                background: '#ffffff',
                border: '1px solid #E6E6E6',
                p: 0,
                mt: 1,
                mb: 2,
                position: 'relative',
                overflow: 'hidden',
                boxShadow:
                  '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
              }}
            >
              {/* Table Header */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 2,
                  borderBottom: '1px solid #E5E7EB',
                  backgroundColor: '#ffffff',
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      color: '#222222',
                      mb: 0,
                      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                    }}
                  >
                    Workflow Run History
                  </Typography>
                </Box>
                    <Tooltip title="Download as CSV" arrow placement="top">
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadCSV}
                    disabled={sortedRows.length === 0}
                    sx={{
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      color: '#2F5D63',
                      borderColor: 'rgba(47, 93, 99, 0.3)',
                      textTransform: 'none',
                      px: 2,
                      py: 0.75,
                      borderRadius: '8px',
                      transition: 'all 0.2s ease',
                      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                      '&:hover': {
                        borderColor: '#2F5D63',
                        backgroundColor: '#DDF3FA',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 8px rgba(47, 93, 99, 0.2)',
                      },
                      '&:active': {
                        transform: 'translateY(0)',
                      },
                      '&.Mui-disabled': {
                        color: '#9CA3AF',
                        borderColor: '#E6E6E6',
                      },
                    }}
                  >
                    Download
                  </Button>
                </Tooltip>
              </Box>

              <TableContainer
                sx={{ backgroundColor: '#ffffff', overflow: 'hidden' }}
              >
                <Table size="small" sx={{
                    width: '100%',
                    '& .MuiTableCell-root': {
                      py: 0.5,
                      px: 2,
                      borderBottom: '1px solid #E6E6E6',
                    },
                    '& thead .MuiTableCell-root': { py: 0.5, px: 1.25 },
                    '& .MuiTableRow-root': { height: 44 },
                  }}>
                  <TableHead sx={{ 
                    background: 'linear-gradient(92.71deg, #205258 -3.62%, #33828B 114.63%)',
                  }}>
                    <TableRow
                      sx={{
                        '&:hover': {
                          background: 'linear-gradient(92.71deg, #205258 -3.62%, #33828B 114.63%)',
                          opacity: 0.95,
                        },
                        transition: 'background-color 0.2s ease',
                      }}
                    >
                      <TableCell
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          py: 1,
                          px: 2,
                          color: '#ffffff',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          borderBottom: '1px solid #E6E6E6',
                          width: '60px',
                        }}
                      >
                        Sr. No
                      </TableCell>
                      
                      {/* Sortable headers */}
                      {[
                        { label: 'Workflow Name', key: 'workflow_name', sortable: true, align: 'left' },
                        { label: 'Run Count', key: 'run_count', sortable: false, align: 'center', width: '100px' },
                        { label: 'Status', key: 'status', sortable: true, align: 'left' },
                        { label: 'Run Mode', key: 'run_mode', sortable: true, align: 'left' },
                        { label: 'Date', key: 'date', sortable: true, align: 'left' },
                        { label: 'Source', key: 'source', sortable: true, align: 'left' },
                        { label: 'Destination', key: 'destination', sortable: true, align: 'left' },
                        { label: 'Success', key: 'success', sortable: true, align: 'center' },
                        { label: 'Failure', key: 'failure', sortable: true, align: 'center' },
                      ].map((header) => (
                        <TableCell
                          key={header.key}
                          align={header.align}
                          onClick={header.sortable ? () => handleSort(header.key) : undefined}
                          sx={{
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            py: 1,
                            px: 2,
                            color: '#ffffff',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            borderBottom: '1px solid #E6E6E6',
                            width: header.width || 'auto',
                            cursor: header.sortable ? 'pointer' : 'default',
                            userSelect: 'none',
                            position: 'relative',
                            '&:hover': header.sortable ? {
                              backgroundColor: 'rgba(255, 255, 255, 0.1)',
                              transition: 'background-color 0.2s ease',
                            } : {},
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: header.align === 'center' ? 'center' : 'space-between' }}>
                            {header.label}
                            {header.sortable && sortConfig.column === header.key && sortConfig.direction === 'asc' && (
                              <ArrowUpwardIcon sx={{ fontSize: '0.875rem', ml: 0.5 }} />
                            )}
                            {header.sortable && sortConfig.column === header.key && sortConfig.direction === 'desc' && (
                              <ArrowDownwardIcon sx={{ fontSize: '0.875rem', ml: 0.5 }} />
                            )}
                            {header.sortable && (!sortConfig.column || sortConfig.column !== header.key) && (
                              <UnfoldMoreIcon sx={{ fontSize: '0.875rem', ml: 0.5, opacity: 0.5 }} />
                            )}
                          </Box>
                        </TableCell>
                      ))}
                      
                      {/* Logs header - not sortable */}
                      <TableCell
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          py: 1,
                          px: 2,
                          color: '#ffffff',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          borderBottom: '1px solid #E6E6E6',
                        }}
                      >
                        Logs
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      Array.from({ length: 6 }).map((_, i) => (
                        <TableRow
                          key={`sk-${i}`}
                          sx={{ borderBottom: '1px solid #F3F4F6' }}
                        >
                          {Array.from({ length: 11 }).map((__, j) => (
                            <TableCell key={`skc-${i}-${j}`}>
                              <Skeleton
                                variant="text"
                                width={
                                  j === 0
                                    ? 20
                                    : j === 1
                                      ? 110
                                      : j === 2
                                        ? 60
                                        : j === 3
                                          ? 80
                                          : 100
                                }
                                height={20}
                              />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : groupedWorkflows.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={11}
                          align="center"
                          sx={{ py: 8, border: 'none' }}
                        >
                          <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.3 }}
                          >
                            <Typography
                              sx={{
                                color: '#222222',
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                              }}
                            >
                              {error || 'No records found for current filters.'}
                            </Typography>
                          </motion.div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      <AnimatePresence>
                        {groupedWorkflows.map((group, groupIdx) => {
                          const groupKey = `${group.workflow_name}_${group.workflow_id}`;
                          const isExpanded = expandedGroups.has(groupKey);
                          const hasMultipleRuns = group.runCount > 1;

                          // Use the most recent run (first in array) for parent row data
                          const latestRun = group.runs[0];
                          const runMode = latestRun?.is_scheduled
                            ? 'SCHEDULED'
                            : 'ON DEMAND';
                          const startDate = latestRun.start_time
                            ? formatLocalDate(new Date(latestRun.start_time))
                            : '-';
                          const { source, destination } = getConnectionNames(group.workflow_id);
                          
                          // Calculate total success and failure counts for the group
                          const totalSuccessCount = group.runs.reduce(
                            (sum, run) => sum + (Number(run.success_record_count) || 0),
                            0
                          );
                          const totalFailureCount = group.runs.reduce(
                            (sum, run) => sum + (Number(run.failure_record_count) || 0),
                            0
                          );

                          return (
                            <React.Fragment key={groupKey}>
                              {/* Parent/Main Row */}
                              <TableRow
                                component={motion.tr}
                                custom={groupIdx}
                                variants={rowVariants}
                                initial="hidden"
                                animate="visible"
                                whileHover="hover"
                                sx={{
                                  borderBottom: '1px solid #F3F4F6',
                                  backgroundColor:
                                    groupIdx % 2 === 1 ? '#F3F5F7' : '#ffffff',
                                  '&:hover': {
                                    backgroundColor: '#DDF3FA',
                                  },
                                }}
                              >
                                <TableCell
                                  sx={{
                                    py: 0.25,
                                    px: 2,
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    color: '#222222',
                                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                                  }}
                                >
                                  {groupIdx + 1}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    py: 0.25,
                                    px: 2,
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    color: '#222222',
                                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1,
                                    }}
                                  >
                                    {hasMultipleRuns && (
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          toggleGroup(
                                            group.workflow_name,
                                            group.workflow_id,
                                          )
                                        }
                                        sx={{
                                          padding: '2px',
                                          color: '#2F5D63',
                                          '&:hover': {
                                            backgroundColor:
                                              'rgba(89, 101, 166, 0.08)',
                                          },
                                        }}
                                      >
                                        {isExpanded ? (
                                          <KeyboardArrowUpIcon
                                            sx={{ fontSize: 18 }}
                                          />
                                        ) : (
                                          <KeyboardArrowDownIcon
                                            sx={{ fontSize: 18 }}
                                          />
                                        )}
                                      </IconButton>
                                    )}
                                    <Link
                                      component={motion.a}
                                      sx={{
                                        color: '#2F5D63',
                                        textDecoration: 'none',
                                        fontWeight: 500,
                                        cursor: 'pointer',
                                        display: 'inline-block',
                                        transformOrigin: 'left center',
                                        willChange: 'transform, color',
                                        backfaceVisibility: 'hidden',
                                        WebkitFontSmoothing: 'antialiased',
                                        MozOsxFontSmoothing: 'grayscale',
                                        textRendering: 'optimizeLegibility',
                                        textShadow: '0 0 0 currentColor',
                                        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                                      }}
                                      style={{
                                        transformPerspective: 1000,
                                      }}
                                      whileHover={{
                                        scaleX: 1.07,
                                        color: '#1F4F55',
                                      }}
                                      whileTap={{ scaleX: 1.04 }}
                                      transition={{
                                        duration: 0.18,
                                        ease: 'easeOut',
                                      }}
                                      onClick={() =>
                                        openConnectionsById(
                                          group.workflow_id,
                                          group.workflow_name,
                                        )
                                      }
                                    >
                                      {group.workflow_name}
                                    </Link>
                                  </Box>
                                </TableCell>
                                <TableCell align="center">
                                  <Chip
                                    label={group.runCount}
                                    size="small"
                                    sx={{
                                      fontSize: '0.75rem',
                                      fontWeight: 700,
                                      borderRadius: '12px',
                                      height: 24,
                                      minWidth: 40,
                                      backgroundColor: hasMultipleRuns
                                        ? '#DDF3FA'
                                        : '#F3F4F6',
                                      color: hasMultipleRuns
                                        ? '#2F5D63'
                                        : '#222222',
                                      border: hasMultipleRuns
                                        ? '1px solid #7CCAD1'
                                        : '1px solid #E6E6E6',
                                      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                                      '& .MuiChip-label': {
                                        px: 1.5,
                                        py: 0.25,
                                      },
                                    }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={
                                      (latestRun.status || latestRun.run_status) === 'ACTIVE'
                                        ? 'RUNNING'
                                        : (latestRun.status || latestRun.run_status || 'N/A')
                                    }
                                    size="small"
                                    sx={{
                                      fontSize: '0.75rem',
                                      fontWeight: 600,
                                      borderRadius: '16px',
                                      height: 28,
                                      minWidth: 80,
                                      backgroundColor:
                                        statusColorMap[
                                          latestRun.status ||
                                            latestRun.run_status
                                        ]?.bgLight || '#F3F4F6',
                                      color:
                                        statusColorMap[
                                          latestRun.status ||
                                            latestRun.run_status
                                        ]?.text || '#222222',
                                      border: `1px solid ${
                                        statusColorMap[
                                          latestRun.status ||
                                            latestRun.run_status
                                        ]?.border || '#D1D5DB'
                                      }`,
                                      textTransform: 'capitalize',
                                      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                                      '& .MuiChip-label': {
                                        px: 2.5,
                                        py: 0.25,
                                      },
                                    }}
                                  />
                                </TableCell>
                                <TableCell
                                  sx={{
                                    py: 0.25,
                                    px: 2,
                                    fontSize: '0.875rem',
                                    color: '#222222',
                                    fontWeight: 400,
                                    verticalAlign: 'middle',
                                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: 'inline-block',
                                      px: 2,
                                      py: 0.2,
                                      borderRadius: '8px',
                                      fontSize: '0.75rem',
                                      fontWeight: 700,
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.025em',
                                      color: '#2F5D63',
                                    }}
                                  >
                                    {runMode}
                                  </Box>
                                </TableCell>
                                <TableCell
                                  sx={{
                                    py: 0.25,
                                    px: 2,
                                    fontSize: '0.875rem',
                                    color: '#222222',
                                    fontWeight: 400,
                                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                                  }}
                                >
                                  {startDate}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    py: 0.25,
                                    px: 2,
                                    fontSize: '0.875rem',
                                    color: '#222222',
                                    fontWeight: 400,
                                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                                  }}
                                >
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    {source.map((name, idx) => (
                                      <Typography key={idx} sx={{ 
                                        fontSize: '0.875rem', 
                                        color: '#222222',
                                        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                                      }}>
                                        {name}
                                      </Typography>
                                    ))}
                                  </Box>
                                </TableCell>
                                <TableCell
                                  sx={{
                                    py: 0.25,
                                    px: 2,
                                    fontSize: '0.875rem',
                                    color: '#222222',
                                    fontWeight: 400,
                                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                                  }}
                                >
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    {destination.map((name, idx) => (
                                      <Typography key={idx} sx={{ 
                                        fontSize: '0.875rem', 
                                        color: '#222222',
                                        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                                      }}>
                                        {name}
                                      </Typography>
                                    ))}
                                  </Box>
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    py: 0.25,
                                    px: 2,
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                    color: statusColorMap.SUCCESS.text,
                                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                                  }}
                                >
                                  {totalSuccessCount.toLocaleString()}
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    py: 0.25,
                                    px: 2,
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                    color: statusColorMap.FAILED.text,
                                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                                  }}
                                >
                                  {totalFailureCount.toLocaleString()}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    py: 0.25,
                                    px: 2,
                                  }}
                                >
                                  <Tooltip
                                    title="View Logs"
                                    arrow
                                    placement="top"
                                  >
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        handleViewLogs(
                                          latestRun.workflow_run_id,
                                        )
                                      }
                                      sx={{
                                        color: '#2F5D63',
                                        '&:hover': {
                                          backgroundColor: '#DDF3FA',
                                        },
                                      }}
                                    >
                                      <Visibility sx={{ fontSize: 20 }} />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>

                              {/* Child Rows (Expanded Individual Runs) */}
                              {hasMultipleRuns &&
                                isExpanded &&
                                group.runs.map((run, runIdx) => {
                                  const childRunMode = run?.is_scheduled
                                    ? 'SCHEDULED'
                                    : 'ON DEMAND';
                                  const childStartDate = run.start_time
                                    ? formatLocalDate(new Date(run.start_time))
                                    : '-';
                                  const { source: childSource, destination: childDestination } = getConnectionNames(group.workflow_id);

                                  return (
                                    <TableRow
                                      key={
                                        run.workflow_run_id ||
                                        `${groupKey}-${runIdx}`
                                      }
                                      component={motion.tr}
                                      initial={{ opacity: 0, height: 0 }}
                                      animate={{ opacity: 1, height: 'auto' }}
                                      exit={{ opacity: 0, height: 0 }}
                                      transition={{ duration: 0.2 }}
                                      sx={{
                                        borderBottom: '1px solid #F3F4F6',
                                        backgroundColor: '#FAFBFC',
                                        '&:hover': {
                                          backgroundColor: '#F0F4F8',
                                        },
                                      }}
                                    >
                                      <TableCell
                                        sx={{
                                          py: 0.25,
                                          px: 2,
                                          fontSize: '0.8rem',
                                          fontWeight: 400,
                                          color: '#222222',
                                          paddingLeft: 4,
                                          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                                        }}
                                      >
                                        {groupIdx + 1}.{runIdx + 1}
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          py: 0.25,
                                          px: 2,
                                          fontSize: '0.8rem',
                                          fontWeight: 400,
                                          color: '#222222',
                                          paddingLeft: 6,
                                          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            fontSize: '0.8rem',
                                            color: '#222222',
                                            fontStyle: 'italic',
                                            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                                          }}
                                        >
                                          Run #{runIdx + 1}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="center">
                                        <Typography
                                          sx={{
                                            fontSize: '0.75rem',
                                            color: '#222222',
                                            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                                          }}
                                        >
                                          -
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Chip
                                          label={
                                            (run.status || run.run_status) === 'ACTIVE'
                                              ? 'RUNNING'
                                              : (run.status || run.run_status || 'N/A')
                                          }
                                          size="small"
                                          sx={{
                                            fontSize: '0.7rem',
                                            fontWeight: 600,
                                            borderRadius: '14px',
                                            height: 24,
                                            minWidth: 70,
                                            backgroundColor:
                                              statusColorMap[
                                                run.status || run.run_status
                                              ]?.bgLight || '#F3F4F6',
                                            color:
                                              statusColorMap[
                                                run.status || run.run_status
                                              ]?.text || '#222222',
                                            border: `1px solid ${
                                              statusColorMap[
                                                run.status || run.run_status
                                              ]?.border || '#D1D5DB'
                                            }`,
                                            textTransform: 'capitalize',
                                            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                                            '& .MuiChip-label': {
                                              px: 2,
                                              py: 0.25,
                                            },
                                          }}
                                        />
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          py: 0.25,
                                          px: 2,
                                          fontSize: '0.8rem',
                                          color: '#222222',
                                          fontWeight: 400,
                                          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: 'inline-block',
                                            px: 1.5,
                                            py: 0.2,
                                            borderRadius: '6px',
                                            fontSize: '0.7rem',
                                            fontWeight: 700,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.025em',
                                            color: '#2F5D63',
                                          }}
                                        >
                                          {childRunMode}
                                        </Box>
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          py: 0.25,
                                          px: 2,
                                          fontSize: '0.8rem',
                                          color: '#222222',
                                          fontWeight: 400,
                                          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                                        }}
                                      >
                                        {childStartDate}
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          py: 0.25,
                                          px: 2,
                                          fontSize: '0.8rem',
                                          color: '#222222',
                                          fontWeight: 400,
                                          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                                        }}
                                      >
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                          {childSource.map((name, idx) => (
                                            <Typography key={idx} sx={{ 
                                              fontSize: '0.8rem', 
                                              color: '#222222',
                                              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                                            }}>
                                              {name}
                                            </Typography>
                                          ))}
                                        </Box>
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          py: 0.25,
                                          px: 2,
                                          fontSize: '0.8rem',
                                          color: '#222222',
                                          fontWeight: 400,
                                          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                                        }}
                                      >
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                          {childDestination.map((name, idx) => (
                                            <Typography key={idx} sx={{ 
                                              fontSize: '0.8rem', 
                                              color: '#222222',
                                              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                                            }}>
                                              {name}
                                            </Typography>
                                          ))}
                                        </Box>
                                      </TableCell>
                                       <TableCell
                                         align="center"
                                         sx={{
                                           py: 0.25,
                                           px: 2,
                                           fontSize: '0.8rem',
                                           fontWeight: 600,
                                           color: statusColorMap.SUCCESS.text,
                                           fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                                         }}
                                       >
                                         {(run.success_record_count ?? 0).toLocaleString()}
                                       </TableCell>
                                       <TableCell
                                         align="center"
                                         sx={{
                                           py: 0.25,
                                           px: 2,
                                           fontSize: '0.8rem',
                                           fontWeight: 600,
                                           color: statusColorMap.FAILED.text,
                                           fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                                         }}
                                       >
                                         {(run.failure_record_count ?? 0).toLocaleString()}
                                       </TableCell>
                                      <TableCell
                                        sx={{
                                          py: 0.25,
                                          px: 2,
                                        }}
                                      >
                                        <Tooltip
                                          title="View Logs"
                                          arrow
                                          placement="top"
                                        >
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              handleViewLogs(
                                                run.workflow_run_id,
                                              )
                                            }
                                            sx={{
                                              color: '#2F5D63',
                                              '&:hover': {
                                                backgroundColor:
                                                  'rgba(89, 101, 166, 0.08)',
                                              },
                                            }}
                                          >
                                            <Visibility sx={{ fontSize: 18 }} />
                                          </IconButton>
                                        </Tooltip>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                            </React.Fragment>
                          );
                        })}
                      </AnimatePresence>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {/* Logs Viewer Dialog */}
              <LogsViewer
                open={logsDialogOpen}
                onClose={() => setLogsDialogOpen(false)}
                workflowRunId={selectedWorkflowRunId}
              />
            </Box>
          </motion.div>
        </Box>
      </Box>
      <OnboardingTour />
    </motion.div>
  );
};

export default Reports;
